import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";
import { ClientStoreV2, LocalTransport } from "@moment/reboot-differential";

import { importAllCsvAttachments } from "../attachments";
import { $document, docsListCollabEntry, docsListItemRemoteEntry } from "../document-list";
import { $remoteWorkspaceFromDocsApi } from "../document-metadata/document-metadata.atom";
import { store as globalStore } from "../store";
import { $documentStoreV2Connection } from "./document-store-v2-connection.atom";

const logger = new Logger("document-store-v2");

/** Per-document singleton local stores for non-collab documents. */
const localStores = new Map<string, ClientStoreV2>();

/**
 * Resolves the canonical ClientStoreV2 for a document.
 *
 * - For collab docs: returns the Reboot-connected store from $documentStoreV2Connection
 * - For local-only docs: returns a cached in-memory store singleton
 * - Returns null if the document metadata is not yet loaded
 */
export const $documentStoreV2 = atomFamily((documentId: string) => {
	const storeAtom = atom(async (get): Promise<ClientStoreV2 | null> => {
		if (!documentId) {
			logger.debug("no documentId");
			return null;
		}

		const doc = await get($document(documentId));
		if (!doc) {
			logger.debug("no doc", { documentId });
			return null;
		}

		const remoteEntry = docsListItemRemoteEntry(doc);
		const collabEntry = remoteEntry ? docsListCollabEntry(remoteEntry) : undefined;

		if (!collabEntry) {
			let localStore = localStores.get(documentId);
			if (!localStore) {
				localStore = new ClientStoreV2({}, new LocalTransport());
				localStores.set(documentId, localStore);
				logger.info("created local store", { documentId });

				// Hydrate the store with any existing CSV attachments on disk.
				await importAllCsvAttachments(documentId, localStore, globalStore.set);
			}
			return localStore;
		}

		// Transitioning to collab — clean up the local store if one existed.
		if (localStores.has(documentId)) {
			localStores.delete(documentId);
			logger.info("deleted local store on collab transition", { documentId });
		}

		const workspace = await get($remoteWorkspaceFromDocsApi({ documentId }));
		const workspaceSlug = remoteEntry?.workspace.slug ?? workspace?.slug;
		const branch = collabEntry.branch;
		const sha = collabEntry.sha;

		if (!workspaceSlug || !branch || !sha) {
			logger.debug("missing collab props", { documentId, workspaceSlug, branch, sha });
			return null;
		}

		logger.info("awaiting connection", { documentId, workspaceSlug, branch, sha });

		const connection = await get(
			$documentStoreV2Connection({ workspaceSlug, documentId, branch, sha })
		);

		logger.info("connection resolved", { documentId, hasStore: !!connection?.store });

		return connection?.store ?? null;
	});

	storeAtom.debugLabel = `documentStoreV2/${documentId}`;
	return storeAtom;
});
